import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    House,
    UserCircle,
    Receipt,
    ChatCircleDots,
    Users,
    SignOut,
    List as MenuIcon,
    X,
    UploadSimple,
    Buildings,
    ChartLine,
    Clock,
    Broadcast,
    CalendarCheck,
    Trophy,
    ClipboardText,
    Megaphone,
} from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useAnnouncements } from "@/context/AnnouncementsContext";
import { Badge } from "@/components/ui/badge";

const techNav = [
    { to: "/dashboard", label: "Dashboard", icon: House },
    { to: "/notices", label: "Notices", icon: Megaphone },
    { to: "/time-tracking", label: "Time Tracking", icon: Clock },
    { to: "/leaderboard", label: "Work Streaks", icon: Trophy },
    { to: "/profile", label: "My Profile", icon: UserCircle },
    { to: "/reports", label: "Payment Reports", icon: Receipt },
    { to: "/requests", label: "My Requests", icon: ChatCircleDots },
];

const adminNav = [
    { to: "/admin", label: "Dashboard", icon: House },
    { to: "/admin/charts", label: "Charts & Trends", icon: ChartLine },
    { to: "/admin/time-records", label: "Time Records", icon: Clock },
    { to: "/admin/live-shifts", label: "Live Shift Board", icon: Broadcast },
    { to: "/admin/weekly-summary", label: "Weekly Attendance", icon: CalendarCheck },
    { to: "/admin/attendance", label: "Attendance Status", icon: ClipboardText },
    { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
    { to: "/leaderboard", label: "Work Streaks", icon: Trophy },
    { to: "/time-tracking", label: "My Time Tracking", icon: Clock },
    { to: "/admin/users", label: "Technicians", icon: Users },
    { to: "/admin/reports", label: "Payment Reports", icon: Receipt },
    { to: "/admin/import-csv", label: "Import Payroll CSV", icon: UploadSimple },
    { to: "/admin/sites", label: "Sites", icon: Buildings },
    { to: "/admin/requests", label: "Requests", icon: ChatCircleDots },
    { to: "/profile", label: "My Profile", icon: UserCircle },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { unread } = useAnnouncements();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const isAdmin = user?.role === "admin";
    const items = isAdmin ? adminNav : techNav;

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/40 z-30"
                    onClick={() => setSidebarOpen(false)}
                    data-testid="sidebar-overlay"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-[#003366] text-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                }`}
                data-testid="app-sidebar"
            >
                <div
                    className="flex items-center justify-between px-5 py-5 border-b border-white/10"
                    style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}
                >
                    <Link to="/" className="flex items-center gap-2.5">
                        <Logo size={36} data-testid="sidebar-logo" />
                        <div className="leading-tight">
                            <div className="font-bold text-base tracking-tight" style={{ fontFamily: "Outfit" }}>
                                ADVANCE
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                                Networks LLC
                            </div>
                        </div>
                    </Link>
                    <button
                        className="lg:hidden text-white/80 hover:text-white"
                        onClick={() => setSidebarOpen(false)}
                        data-testid="sidebar-close-button"
                    >
                        <X size={22} />
                    </button>
                </div>

                <nav className="flex-1 px-3 py-4 space-y-1">
                    {items.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end
                                className={({ isActive }) =>
                                    `an-sidebar-link ${isActive ? "active" : ""}`
                                }
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <Icon size={18} weight="regular" />
                                <span>{item.label}</span>
                                {item.to === "/notices" && unread > 0 && (
                                    <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#E6002C] px-1.5 text-[11px] font-semibold text-white"
                                        data-testid="nav-notices-unread-badge">
                                        {unread}
                                    </span>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="px-4 py-4 border-t border-white/10">
                    <div className="mb-3">
                        <div className="text-xs text-white/60 uppercase tracking-wider">Signed in as</div>
                        <div className="text-sm font-medium truncate">{user?.full_name}</div>
                        <Badge
                            className="mt-1.5 bg-[#0066D9] hover:bg-[#0066D9] text-white text-[10px] uppercase tracking-wider"
                            data-testid="user-role-badge"
                        >
                            {user?.role}
                        </Badge>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-[#E6002C] transition-colors text-sm font-medium"
                        data-testid="logout-button"
                    >
                        <SignOut size={16} />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar (mobile) */}
                <header
                    className="lg:hidden sticky top-0 z-20 bg-white border-b border-[#E2E8F0] flex items-center justify-between px-4 py-3"
                    style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
                >
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="text-[#003366] p-1.5"
                        aria-label="Open menu"
                        data-testid="sidebar-open-button"
                    >
                        <MenuIcon size={22} />
                    </button>
                    <div className="flex items-center gap-2">
                        <Logo size={26} data-testid="topbar-logo" />
                        <span className="font-bold text-sm text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            ADVANCE NETWORKS
                        </span>
                    </div>
                    <div className="w-8" />
                </header>

                <main
                    className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-10 an-fade-in"
                    style={{ paddingBottom: "calc(3rem + env(safe-area-inset-bottom))" }}
                >
                    <div className="max-w-7xl mx-auto">{children}</div>
                </main>
            </div>
        </div>
    );
}
